import type { ReactNode } from 'react'

import type { FileStorageStatus } from 'generated/graphql'
import type { IDocument, IUploadProgress } from 'modules/documents/common/documents.types'

export enum FileUploadStatus {
  Uploading = 'UPLOADING',
  Failed = 'FAILED',
}

export type FileStatus = FileStorageStatus | FileUploadStatus

/**
 * Represents an individual file within an upload session
 */
export interface IUploadFile {
  id: string
  file: File
  fileName: string
  size: number
  status: FileStatus
  progress: number // 0-100 percentage
  error?: Error
}

/**
 * Represents a single upload session (batch of files)
 */
export interface IUploadSession {
  id: string
  status: 'queued' | 'uploading' | 'completed' | 'cancelled' | 'failed'
  files: IUploadFile[]
  createdAt: Date
  startedAt?: Date
  completedAt?: Date
}

/**
 * Queue state managing all upload sessions
 */
export interface IUploadQueue {
  sessions: IUploadSession[] // All sessions (active + history)
  activeSessionIds: string[] // Currently uploading session IDs
}

export interface IStorageInfo {
  used: number
  limit: number
}

export interface DocumentsManagementContextData {
  documents: IDocument[]
  isLoading: boolean
  isUploading: boolean
  uploadProgress: IUploadProgress
  error: boolean
  storageInfo: IStorageInfo
  cursor: string | null
  hasMore: boolean
  isFetchingMore: boolean
  totalCount: number

  fetchDocuments: () => Promise<void>
  uploadDocuments: (files: File[]) => Promise<void>
  deleteDocument: (documentId: string) => Promise<void>
  refreshStorageInfo: () => Promise<void>
  fetchMoreDocuments: () => Promise<void>
  refreshDocuments: () => Promise<void>
}

export interface DocumentsManagementProviderProps {
  children: ReactNode
}

export type DocumentData = {
  __typename?: 'FileStorageFile'
  fileName: string
  id: string
  size?: number | null
  createdAt: any
  status: FileStorageStatus
}
